import React, { useEffect, useRef } from 'react'
import { prefersReducedMotion } from '../../utils/scroll'

const LINK_DISTANCE = 140
const MOUSE_RADIUS = 180

// Animated neural-network field: drifting nodes that link up and lean toward the cursor.
const NeuralCanvas = () => {
  const canvasRef = useRef(null)

  useEffect(() => {
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    const reduced = prefersReducedMotion()
    const mouse = { x: -9999, y: -9999 }
    let nodes = []
    let width = 0
    let height = 0
    let frame = 0
    let running = true

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      width = canvas.offsetWidth
      height = canvas.offsetHeight
      canvas.width = width * dpr
      canvas.height = height * dpr
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      const count = Math.min(90, Math.floor((width * height) / 14000))
      nodes = Array.from({ length: count }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.35,
        vy: (Math.random() - 0.5) * 0.35,
        r: Math.random() * 1.6 + 0.8,
        pulse: Math.random() * Math.PI * 2
      }))
    }

    const draw = () => {
      const dark = document.documentElement.classList.contains('dark-mode')
      const node = dark ? '167, 139, 250' : '109, 40, 217'
      const link = dark ? '34, 211, 238' : '8, 145, 178'
      ctx.clearRect(0, 0, width, height)

      for (let i = 0; i < nodes.length; i += 1) {
        const a = nodes[i]
        if (!reduced) {
          const dx = mouse.x - a.x
          const dy = mouse.y - a.y
          const dist = Math.hypot(dx, dy)
          if (dist < MOUSE_RADIUS) {
            a.vx += (dx / dist) * 0.012
            a.vy += (dy / dist) * 0.012
          }
          a.vx *= 0.992
          a.vy *= 0.992
          a.x += a.vx
          a.y += a.vy
          if (a.x < 0 || a.x > width) a.vx *= -1
          if (a.y < 0 || a.y > height) a.vy *= -1
          a.pulse += 0.03
        }

        for (let j = i + 1; j < nodes.length; j += 1) {
          const b = nodes[j]
          const d = Math.hypot(a.x - b.x, a.y - b.y)
          if (d < LINK_DISTANCE) {
            ctx.strokeStyle = `rgba(${link}, ${(1 - d / LINK_DISTANCE) * 0.28})`
            ctx.lineWidth = 0.8
            ctx.beginPath()
            ctx.moveTo(a.x, a.y)
            ctx.lineTo(b.x, b.y)
            ctx.stroke()
          }
        }

        const mouseDist = Math.hypot(mouse.x - a.x, mouse.y - a.y)
        const glow = mouseDist < MOUSE_RADIUS ? 1 - mouseDist / MOUSE_RADIUS : 0
        ctx.fillStyle = `rgba(${node}, ${0.45 + Math.sin(a.pulse) * 0.2 + glow * 0.35})`
        ctx.beginPath()
        ctx.arc(a.x, a.y, a.r + glow * 1.8, 0, Math.PI * 2)
        ctx.fill()
      }

      if (running && !reduced) frame = requestAnimationFrame(draw)
    }

    const onMove = (event) => {
      const rect = canvas.getBoundingClientRect()
      mouse.x = event.clientX - rect.left
      mouse.y = event.clientY - rect.top
    }
    const onLeave = () => { mouse.x = -9999; mouse.y = -9999 }

    // Pause the loop while the hero is off-screen.
    const visibility = new IntersectionObserver(([entry]) => {
      const shouldRun = entry.isIntersecting
      if (shouldRun && !running) {
        running = true
        frame = requestAnimationFrame(draw)
      } else if (!shouldRun) {
        running = false
        cancelAnimationFrame(frame)
      }
    })

    resize()
    draw()
    visibility.observe(canvas)
    window.addEventListener('resize', resize)
    window.addEventListener('pointermove', onMove)
    document.addEventListener('pointerleave', onLeave)

    return () => {
      running = false
      cancelAnimationFrame(frame)
      visibility.disconnect()
      window.removeEventListener('resize', resize)
      window.removeEventListener('pointermove', onMove)
      document.removeEventListener('pointerleave', onLeave)
    }
  }, [])

  return <canvas ref={canvasRef} className="neural-canvas" aria-hidden="true" />
}

export default NeuralCanvas
